import { useCallback, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Chip,
  DataTable,
  Icon,
  IconButton,
  Menu,
  Meter,
  Popover,
  type Column,
  type MenuItem,
  type Tone,
} from '@w7/ui'
import { AccountCell } from '@/app/AppShell'
import { Amount, FlagChips, Money } from '@/app/bits'
import { limitUsage, round2 } from '@/lib/book'
import { EMPTY, ago, count, lll, money, percent } from '@/lib/format'
import { isAgent, ROLE_LABEL, type Account, type AccountStatus } from '@/data/types'
import type { DownlineResult } from '@/data/api'
import {
  labRateDisplayValue,
  labRateGameLabel,
  labRateRowsForInfo,
} from '@/api/map/lab-rate'
import { isDirect } from './settle'
import { MemberEventsModal } from './MemberEventsModal'
import { GameLockModal } from './GameLockModal'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { canCreditTransfer, canEditDownline, canGlobalLock } from '@/app/staffAccess'
import { canShowGameLock } from './gameLockAccess'
import { canShowMemberEvents } from './memberEventsAccess'
import { showMemberListExposure } from './memberListExposure'

/**
 * The flat downline table.
 *
 * Two surfaces share it. `Direct only` is the SETTLING surface: row selection,
 * labelled Give and Take buttons, and a settlement figure that is itself a
 * click-to-zero button. The match list (whole downline + a filter) is the same
 * table read-only, because a filter produces a list and browsing produces a
 * tree — and a lazily-loaded tree that is also filtered would have to fetch
 * ancestors nobody expanded, giving you a tree with holes in it.
 *
 * The give/take controls are ABSENT on rows that are not direct children, not
 * disabled. The original disabled them and appended a 90-character explanation
 * to the aria-label of every one of 120 rows; the page now says it once, in a
 * quiet line under the toolbar.
 */

export type SortId =
  | 'username'
  | 'creditLimit'
  | 'balance'
  | 'exposure'
  | 'settlement'
  | 'lastActiveAt'
export type SortDirection = 'asc' | 'desc'

export const SORTABLE: { id: SortId; defaultDirection: SortDirection }[] = [
  { id: 'username', defaultDirection: 'asc' },
  { id: 'creditLimit', defaultDirection: 'desc' },
  { id: 'balance', defaultDirection: 'desc' },
  { id: 'exposure', defaultDirection: 'desc' },
  { id: 'settlement', defaultDirection: 'desc' },
  { id: 'lastActiveAt', defaultDirection: 'desc' },
]

/**
 * Status is not money, so it never borrows the money palette: in this console
 * green means the book won and red means it paid out, and a green "Active" chip
 * beside a red balance makes both harder to read.
 */
export const STATUS: Record<AccountStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'info' },
  inactive: { label: 'Inactive', tone: 'muted' },
  suspended: { label: 'Suspended', tone: 'warn' },
  closed: { label: 'Closed', tone: 'muted' },
}

/**
 * Why a row cannot be ticked. Every reason is stated on the disabled checkbox
 * itself rather than the box being removed: a missing box breaks the column and
 * the rows stop scanning as a grid.
 *
 * Suspended accounts stay SELECTABLE on purpose — settling up with a suspended
 * agent is very often exactly why they were suspended.
 */
export function selectionBlock(row: Account, self: Account | undefined): string | null {
  if (!isDirect(row, self)) {
    return `${row.username} sits further down — credit moves only between you and your direct children`
  }
  if (row.settlement === 0) return `${row.username} has nothing outstanding to settle`
  if (row.status === 'closed') return `${row.username}'s account is closed`
  return null
}

export interface DownlineTableProps {
  rows: Account[]
  totals: DownlineResult['totals'] | undefined
  self: Account | undefined
  loading: boolean
  /** Omit to inherit Appearance → Table density. */
  density?: 'compact' | 'default'
  sort: SortId
  direction: SortDirection
  onSort: (id: SortId, next: SortDirection) => void
  /** Present only on the direct-only surface. Its absence is what removes bulk settle. */
  selection?: { selected: Set<string>; onChange: (next: Set<string>) => void }
  /** Match-list mode: "under ravi_07 › k_master" under the username. */
  lineageOf?: (row: Account) => string | null
  onShowInTree?: (row: Account) => void
  onTransfer: (row: Account, direction: 'give' | 'take', amount?: number) => void
  /** When book-wide global lock is on, show per-row except/re-lock column. */
  bookLocked?: boolean
  onToggleRowGlobalLock?: (row: Account, unlocking: boolean) => void
  empty: ReactNode
}

export function DownlineTable({
  rows,
  totals,
  self,
  loading,
  density,
  sort,
  direction,
  onSort,
  selection,
  lineageOf,
  onShowInTree,
  onTransfer,
  bookLocked = false,
  onToggleRowGlobalLock,
  empty,
}: DownlineTableProps) {
  const navigate = useNavigate()
  const staffMe = useStaffMe()
  const allowEditDownline = staffMe.isSuccess && canEditDownline(staffMe.data)
  const allowCreditTransfer = staffMe.isSuccess && canCreditTransfer(staffMe.data)
  const allowGlobalLockWrite = staffMe.isSuccess && canGlobalLock(staffMe.data)
  const [eventsFor, setEventsFor] = useState<Account | null>(null)
  const [gameLockFor, setGameLockFor] = useState<Account | null>(null)
  const showEvents = canShowMemberEvents(self?.role)
  const showLock = canShowGameLock(self?.role)
  const showGlobalLockCol = bookLocked && Boolean(onToggleRowGlobalLock)
  const sortHeader = (id: SortId, label: string, align: 'left' | 'right' = 'right') => (
    <SortHeader
      label={label}
      align={align}
      active={sort === id}
      direction={direction}
      defaultDirection={SORTABLE.find((entry) => entry.id === id)?.defaultDirection ?? 'desc'}
      onSort={(next) => onSort(id, next)}
    />
  )

  const columns: Column<Account>[] = [
    {
      id: 'account',
      header: sortHeader('username', 'Agent or member', 'left'),
      width: 240,
      total: totals ? (
        <span className="w7-dim">
          {count(totals.agents)} agents · {count(totals.members)} members
        </span>
      ) : (
        EMPTY
      ),
      cell: (row) => {
        const lineage = lineageOf?.(row)
        return (
          <AccountCell
            username={row.username}
            displayName={row.displayName}
            role={row.role}
            to={`/member/${row.id}`}
            meta={
              <>
                {isAgent(row.role) ? `${count(row.memberCount)} members · ` : null}
                {row.displayName}
                {lineage ? <span className="w7-dl-lineage">{lineage}</span> : null}
              </>
            }
          />
        )
      },
    },
    {
      id: 'creditLimit',
      header: sortHeader('creditLimit', 'Credit limit'),
      numeric: true,
      hideBelow: 900,
      help: 'The ceiling you set on what this account may hold.',
      total: (
        <Amount value={totals?.creditLimit} currency={self?.currencyType} alreadyDisplay />
      ),
      cell: (row) => (
        <Amount value={row.creditLimit} currency={row.currencyType} alreadyDisplay />
      ),
    },
    {
      id: 'balance',
      header: sortHeader('balance', 'Balance'),
      numeric: true,
      help: 'What they hold right now. Neither good news nor bad — just a position.',
      total: (
        <Amount value={totals?.balance} currency={self?.currencyType} alreadyDisplay />
      ),
      cell: (row) => (
        <Amount value={row.balance} currency={row.currencyType} alreadyDisplay />
      ),
    },
    {
      id: 'exposure',
      header: sortHeader('exposure', 'Exposure'),
      numeric: true,
      hideBelow: 560,
      help:
        'Worst case across their open bets, drawn against their credit limit. ' +
        'The continuously-polled live book is on the Exposure screen.',
      total: totals ? (
        <UsageCell
          exposure={totals.exposure}
          creditLimit={totals.creditLimit}
          currency={self?.currencyType}
        />
      ) : EMPTY,
      cell: (row) =>
        showMemberListExposure(row.role) ? (
          <UsageCell
            exposure={row.exposure}
            creditLimit={row.creditLimit}
            currency={row.currencyType}
          />
        ) : (
          <span className="w7-dim">NA</span>
        ),
    },
    {
      id: 'take',
      header: sortHeader('settlement', 'Take'),
      numeric: true,
      help:
        'What they owe you. Always shows a figure (0 when clear). Click on a direct child to open Take.',
      total: totals ? (
        <Money value={totals.toCollect} tone="profit" currency={self?.currencyType} alreadyDisplay />
      ) : EMPTY,
      cell: (row) => (
        <GiveTakeAmountCell
          row={row}
          self={self}
          direction="take"
          onTransfer={onTransfer}
          allowTransfer={allowCreditTransfer}
        />
      ),
      width: 110,
    },
    {
      id: 'give',
      header: 'Give',
      numeric: true,
      help:
        'What you owe them. Always shows a figure (0 when clear). Click on a direct child to open Give.',
      total: totals ? (
        <Money value={totals.toPay} tone="loss" currency={self?.currencyType} alreadyDisplay />
      ) : EMPTY,
      cell: (row) => (
        <GiveTakeAmountCell
          row={row}
          self={self}
          direction="give"
          onTransfer={onTransfer}
          allowTransfer={allowCreditTransfer}
        />
      ),
      width: 110,
    },
    {
      id: 'status',
      header: 'Status',
      hideBelow: 900,
      cell: (row) => (
        <Chip tone={STATUS[row.status].tone} size="sm">
          {STATUS[row.status].label}
        </Chip>
      ),
    },
    {
      id: 'info',
      header: 'Info',
      width: 56,
      help: 'Partnership rates by game — Lab Member List Info.',
      cell: (row) => <RateInfoCell account={row} />,
    },
    ...(showGlobalLockCol
      ? [
          {
            id: 'global-lock',
            header: 'Global Lock',
            width: 88,
            align: 'center',
            help: 'Except or re-lock this account while book-wide Global Lock is on (needs Global Lock).',
            cell: (row: Account) => {
              const closed = row.status === 'closed'
              const locked = row.globalLock === true
              const viewOnly = !allowGlobalLockWrite
              return (
                <button
                  type="button"
                  className={`w7-events-lock${locked ? ' is-locked' : ' is-unlocked'}${closed || viewOnly ? ' is-muted' : ''}`}
                  disabled={closed || viewOnly}
                  aria-label={
                    closed
                      ? `${row.username} is closed`
                      : viewOnly
                        ? locked
                          ? `${row.username} globally locked (view only)`
                          : `${row.username} not globally locked (view only)`
                        : locked
                          ? `Unlock ${row.username} globally`
                          : `Lock ${row.username} globally`
                  }
                  title={
                    viewOnly
                      ? 'View only — needs Global Lock'
                      : locked
                        ? 'Globally locked — click to except this account'
                        : 'Excepted from global lock — click to lock again'
                  }
                  onClick={(event) => {
                    event.stopPropagation()
                    if (closed || viewOnly) return
                    onToggleRowGlobalLock?.(row, locked)
                  }}
                >
                  <Icon name={locked ? 'lock' : 'unlock'} size={18} />
                </button>
              )
            },
          } satisfies Column<Account>,
        ]
      : []),
    ...(showLock
      ? [
          {
            id: 'lock',
            header: 'Lock',
            width: 72,
            align: 'center',
            help: 'View or change game locks for this account (write needs Member Lock/Unlock grant).',
            cell: (row: Account) => (
              <IconButton
                name="lock"
                size="sm"
                label={`Games lock for ${row.username}`}
                title="View or change game locks for this user"
                onClick={(event) => {
                  event.stopPropagation()
                  setGameLockFor(row)
                }}
              />
            ),
          } satisfies Column<Account>,
        ]
      : []),
    ...(showEvents
      ? [
          {
            id: 'events',
            header: 'Events',
            width: 72,
            align: 'center',
            help: 'Enable or disable cricket matches for this account.',
            cell: (row: Account) => (
              <IconButton
                name="calendar"
                size="sm"
                label={`Events for ${row.username}`}
                title="Enable or disable matches for this user"
                onClick={(event) => {
                  event.stopPropagation()
                  setEventsFor(row)
                }}
              />
            ),
          } satisfies Column<Account>,
        ]
      : []),
    {
      id: 'lastActive',
      header: sortHeader('lastActiveAt', 'Last active'),
      numeric: true,
      hideBelow: 1080,
      cell: (row) => (
        <span title={row.lastActiveAt ? lll(row.lastActiveAt) : 'Has never signed in'}>
          {ago(row.lastActiveAt)}
        </span>
      ),
    },
    {
      id: 'flags',
      header: 'Attention',
      hideBelow: 1280,
      help: 'Operational signals only — credit, dormancy and settlement.',
      cell: (row) =>
        row.flags.length === 0 ? <span className="w7-dimmer">Clear</span> : <FlagChips flags={row.flags} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right',
      width: 136,
      help: 'Open the full record or edit terms.',
      // Deliberately last and never hidden: the table renders row actions inside
      // the final column.
      cell: () => null,
    },
  ]

  return (
    <>
    <DataTable
      label={
        selection
          ? 'Your direct downline, one row per agent or member'
          : 'Everyone beneath you matching these filters, one row per agent or member'
      }
      rows={rows}
      columns={columns}
      rowKey={(row) => row.id}
      density={density}
      loading={loading}
      skeletonRows={10}
      showTotals
      selectable={
        selection
          ? {
              selected: selection.selected,
              onChange: selection.onChange,
              isSelectable: (row) => selectionBlock(row, self) === null,
              disabledReason: (row) =>
                selectionBlock(row, self) ?? `${row.username} cannot be selected`,
              rowLabel: (row) => `Select ${row.username}`,
            }
          : undefined
      }
      rowTone={(row) =>
        row.flags.includes('exposure-over-limit')
          ? 'loss'
          : row.flags.includes('exposure-near-limit')
            ? 'warn'
            : undefined
      }
      rowActions={(row) => {
        const direct = isDirect(row, self)
        const items: (MenuItem | 'separator')[] = [
          {
            id: 'open',
            label: isAgent(row.role) ? 'Open agent' : 'Open member',
            icon: 'user',
            onSelect: () => navigate(`/member/${row.id}`),
          },
          ...(direct && allowEditDownline && row.status !== 'closed'
            ? [
                {
                  id: 'edit',
                  label: 'Edit',
                  icon: 'edit' as const,
                  onSelect: () => navigate(`/member/${row.id}?tab=edit`),
                },
              ]
            : []),
          {
            id: 'statement',
            label: 'Statement',
            icon: 'transfers',
            onSelect: () => navigate(`/member/${row.id}?tab=statement`),
          },
          {
            id: 'bets',
            label: 'Bets',
            icon: 'sport',
            onSelect: () => navigate(`/member/${row.id}?tab=bets`),
          },
        ]
        if (onShowInTree) {
          items.push('separator', {
            id: 'tree',
            label: 'Show in tree',
            icon: 'tree',
            onSelect: () => onShowInTree(row),
          })
        }

        return (
          <>
            {direct && allowEditDownline && row.status !== 'closed' ? (
              <Button
                size="sm"
                variant="secondary"
                icon="edit"
                aria-label={`Edit ${row.username}`}
                onClick={() => navigate(`/member/${row.id}?tab=edit`)}
              >
                Edit
              </Button>
            ) : null}
            <Menu label={`More for ${row.username}`} align="end" items={items} />
          </>
        )
      }}
      empty={empty}
    />
    {eventsFor ? (
      <MemberEventsModal
        userId={eventsFor.id}
        username={eventsFor.username}
        onClose={() => setEventsFor(null)}
      />
    ) : null}
    {gameLockFor ? (
      <GameLockModal
        account={rows.find((row) => row.id === gameLockFor.id) ?? gameLockFor}
        onClose={() => setGameLockFor(null)}
      />
    ) : null}
    </>
  )
}

/* -------------------------------------------------------------- fragments -- */

/** Lab view-member Info — portaled Popover (table cells clip absolute CSS panels). */
export function RateInfoCell({ account }: { account: Account }) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const rates = labRateRowsForInfo(account.rate)
  const ccy = account.currencyType ?? 'INR'
  return (
    <div className="w7-rate-info">
      <IconButton
        ref={anchor}
        name="info"
        size="sm"
        label={`Rate info for ${account.username}`}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((v) => !v)
        }}
      />
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        label={`Rates for ${account.username}`}
        align="end"
        width={220}
      >
        <div className="w7-rate-info-head">
          {ROLE_LABEL[account.role]} — {ccy}
        </div>
        {rates.length === 0 ? (
          <div className="w7-dim">No rates</div>
        ) : (
          rates.map((game) => (
            <div key={game._id} className="w7-rate-info-row">
              <span>{labRateGameLabel(game.game.name)}</span>
              <span>
                {labRateDisplayValue(game, ccy).toFixed(ccy === 'INR' ? 2 : 0)}
              </span>
            </div>
          ))
        )}
      </Popover>
    </div>
  )
}

/**
 * Backend-UI Take / Give as separate columns. Always shows a number (0 when
 * nothing outstanding). On a direct child the figure is clickable and opens
 * the transfer popup — prefilled when the amount is non-zero.
 */
function GiveTakeAmountCell({
  row,
  self,
  direction,
  onTransfer,
  allowTransfer,
}: {
  row: Account
  self: Account | undefined
  direction: 'give' | 'take'
  onTransfer: (row: Account, direction: 'give' | 'take', amount?: number) => void
  allowTransfer: boolean
}) {
  const settlement = round2(row.settlement)
  const amount =
    direction === 'take'
      ? settlement > 0
        ? settlement
        : 0
      : settlement < 0
        ? round2(-settlement)
        : 0

  const tone = direction === 'take' ? 'profit' : amount > 0 ? 'loss' : 'neutral'
  if (!isDirect(row, self) || amount <= 0 || !allowTransfer) {
    return <Money value={amount} tone={tone} currency={row.currencyType} alreadyDisplay />
  }

  return (
    <button
      type="button"
      className="w7-settle"
      data-tone={tone}
      aria-label={
        direction === 'take'
          ? `Take ${money(amount, { currency: row.currencyType, alreadyDisplay: true })} from ${row.username}`
          : `Give ${money(amount, { currency: row.currencyType, alreadyDisplay: true })} to ${row.username}`
      }
      title={`Opens ${direction === 'take' ? 'Take' : 'Give'} with ${money(amount, { currency: row.currencyType, alreadyDisplay: true })} filled in — you can still change it.`}
      onClick={() => onTransfer(row, direction, amount)}
    >
      <Money value={amount} tone={tone} currency={row.currencyType} alreadyDisplay />
    </button>
  )
}

/**
 * Sorting is driven from the header into the QUERY, not into the table.
 *
 * The table sorts the rows it is handed, which is one page of twenty-five; on a
 * downline of a hundred and twenty that would quietly mean "sort this page",
 * which is worse than the original's honest lack of sorting.
 */
function SortHeader({
  label,
  align,
  active,
  direction,
  defaultDirection,
  onSort,
}: {
  label: string
  align: 'left' | 'right'
  active: boolean
  direction: SortDirection
  defaultDirection: SortDirection
  onSort: (next: SortDirection) => void
}) {
  const next: SortDirection = active ? (direction === 'asc' ? 'desc' : 'asc') : defaultDirection
  return (
    <button
      type="button"
      className="w7-th-sort"
      data-align={align}
      aria-label={
        active
          ? `${label}, sorted ${direction === 'asc' ? 'lowest first' : 'highest first'}. Sort ${next === 'asc' ? 'lowest' : 'highest'} first.`
          : `Sort by ${label}`
      }
      onClick={() => onSort(next)}
    >
      {label}
      <Icon
        name={active && direction === 'asc' ? 'chevron-up' : 'chevron-down'}
        size={12}
        style={{ opacity: active ? 1 : 0.28 }}
      />
    </button>
  )
}

/** Exposure against the limit that governs it — the column the original left at 0. */
export function UsageCell({
  exposure,
  creditLimit,
  currency,
}: {
  exposure: number
  creditLimit: number
  currency?: string
}) {
  const usage = limitUsage(exposure, creditLimit)
  return (
    <span className="w7-col" style={{ gap: 3, alignItems: 'flex-end' }}>
      <Amount value={exposure} currency={currency} alreadyDisplay />
      <Meter
        value={usage}
        height={4}
        label={`${percent(usage, 0)} of a ${money(creditLimit, { round: true, currency, alreadyDisplay: true })} credit limit`}
      />
    </span>
  )
}

/**
 * A media query as state, without an effect or a resize listener of our own.
 * The row actions genuinely change shape below 900px — the buttons fold into
 * the Menu — and rendering both and hiding one with CSS would leave a phone
 * operator tabbing through two invisible buttons.
 */
export function useNarrow(maxWidth: number): boolean {
  const query = `(max-width: ${maxWidth}px)`
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {}
      const mql = window.matchMedia(query)
      mql.addEventListener('change', notify)
      return () => mql.removeEventListener('change', notify)
    },
    [query],
  )
  const snapshot = useMemo(
    () => () =>
      typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false,
    [query],
  )
  return useSyncExternalStore(subscribe, snapshot, () => false)
}
